import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  defaultColDef,
  defaultGridOptionsFactory,
} from '@wilson/ag-grid/configuration';
import { AuthorizationModule } from '@wilson/authorization';
import {
  RoleAction,
  RolePermissionSubject,
  User,
  UserQualificationV2,
  UserQualificationV2WithRelation,
  WhereOption,
} from '@wilson/interfaces';
import { EmptyStateComponent } from '@wilson/shared-components';
import { SharedUserQualificationsGateway } from '@wilson/shared-employees';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridOptions,
  IGetRowsParams,
} from 'ag-grid-community';
import { isEqual } from 'lodash';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { SharedUserQualificationDetailViewResponse } from '../../../gateways/shared-user-qualifications-v2.gateway';
import {
  GetSharedUserQualificationsV2Overview,
  GetSharedUserQualificationsV2OverviewDetails,
  OpenSharedUserQualificationV2OverviewDetailsDrawer,
  ResetSharedUserQualificationsV2OverviewDetails,
} from '../../../states/shared-user-qualifications-v2-overview/shared-user-qualifications-v2-overview.action';
import { SharedUserQualificationsV2OverviewState } from '../../../states/shared-user-qualifications-v2-overview/shared-user-qualifications-v2-overview.state';
import { QualificationDetailsDrawerComponent } from '../../qualifications-v2-list/detail-drawer/qualification-details-drawer.component';
import { ConfirmedByUserRendererComponent } from '../../qualifications-v2-list/renderers/confirmed-by-user-renderer';
import { DateRendererComponent } from '../../qualifications-v2-list/renderers/date-renderer.component';
import { QualificationCategoryRendererComponent } from '../../qualifications-v2-list/renderers/qualification-category.renderer';
import { QualificationLinkRendererComponent } from '../../qualifications-v2-list/renderers/qualification-link-renderer.component';
import { QualificationSharedUserRendererComponent } from '../../qualifications-v2-list/renderers/qualification-shared-user-renderer';
import { StatusQualificationV2IconRendererComponent } from '../../qualifications-v2-list/renderers/status-icon.renderer';
import { ValidTillRendererComponent } from '../../qualifications-v2-list/renderers/valid-till-renderer.component';

@Component({
  selector: 'wilson-shared-user-qualifications-v2-list',
  templateUrl: './shared-user-qualifications-v2-list.component.html',
  styleUrls: ['./shared-user-qualifications-v2-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SharedUserQualificationsGateway],
  imports: [
    AgGridAngular,
    AgGridModule,
    CommonModule,
    QualificationDetailsDrawerComponent,
  ],
})
export class SharedUserQualificationsV2ListComponent
  implements OnInit, OnDestroy
{
  private readonly translateService = inject(TranslateService);
  private store = inject(Store);
  private readonly router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  @Input() statusFilter!: BehaviorSubject<string[]>;
  @Input() categoryFilter!: BehaviorSubject<string[]>;
  @Input() userIdsFilter!: BehaviorSubject<string[]>;
  @Input() searchFilter!: BehaviorSubject<string>;
  @Input() partnerFilter!: BehaviorSubject<string[]>;

  public can = inject(AbilityServiceSignal).can;
  public readonly RoleAction = RoleAction;
  public readonly RolePermissionSubject = RolePermissionSubject;

  private filters = {
    statuses: [] as string[],
    category: [] as string[],
    partnerIds: [] as string[],
    userIds: [] as string[],
    search: '',
    lang: '',
  };
  private destroy$ = new Subject<void>();
  private readonly defaultSortingFields: Record<string, 'asc' | 'desc'> | null =
    { userName: 'asc' };
  pageSize = this.store.selectSnapshot(
    SharedUserQualificationsV2OverviewState.getPageSize,
  );

  public readonly defaultColDef = defaultColDef;
  readonly gridOptions: GridOptions = {
    ...defaultGridOptionsFactory(),
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    rowModelType: 'infinite',
    cacheBlockSize: this.pageSize,
    paginationPageSize: this.pageSize,
    pagination: true,
    paginationPageSizeSelector: [50, 100, 150],
    suppressPaginationPanel: false,
    paginationAutoPageSize: false,
    onSortChanged: (params) => this.handleSortChanged(params),
    multiSortKey: 'ctrl',
    loading: true,
  };

  selectedQualification!: UserQualificationV2;
  employee!: User;

  public readonly components = {
    noRowsOverlay: EmptyStateComponent,
    qualificationLinkRendererComponent: QualificationLinkRendererComponent,
    statusIconRenderer: StatusQualificationV2IconRendererComponent,
    confirmedByUserIconRendererComponent: ConfirmedByUserRendererComponent,
    qualificationCategoryRendererComponent:
      QualificationCategoryRendererComponent,
    qualificationSharedUserRendererComponent:
      QualificationSharedUserRendererComponent,
    dateRendererComponent: DateRendererComponent,
    validTillRenderer: ValidTillRendererComponent,
  };

  private gridApi!: GridApi;
  protected columnDefs: ColDef[];
  protected showGrid = false;
  public paginationChangedListener = () => this.handlePaginationChanged();

  qualifications$ = this.store.select(
    SharedUserQualificationsV2OverviewState.getSharedUserQualifications,
  );
  isLoading$ = this.store.select(
    SharedUserQualificationsV2OverviewState.isLoading,
  );
  isDrawerVisible$ = this.store.select(
    SharedUserQualificationsV2OverviewState.isDrawerVisible,
  );
  qualificationDetails$ = this.store
    .select(
      SharedUserQualificationsV2OverviewState.getSharedUserQualificationDetails,
    )
    .pipe(
      tap((quali) => {
        if (quali) {
          this.employee$ = of(quali.user);
        }
      }),
    ) as unknown as Observable<UserQualificationV2WithRelation>;
  employee$!: Observable<Pick<User, 'firstName' | 'lastName'>>;

  constructor() {
    this.columnDefs = [
      {
        flex: 1,
        resizable: true,
        field: 'qualification.name',
        cellRenderer: 'qualificationLinkRendererComponent',
        headerName: this.translateService.instant(
          'page.qualifications.qualification',
        ),
        sortable: true,
        colId: 'qualificationName',
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
      },
      {
        flex: 1,
        resizable: true,
        field: 'user.firstName',
        cellRenderer: 'qualificationSharedUserRendererComponent',
        headerName: this.translateService.instant('page.qualifications.user'),
        sortable: true,
        sort: 'asc',
        colId: 'userName',
      },
      {
        flex: 1,
        resizable: true,
        field: 'partnerName',
        headerName: this.translateService.instant(
          'page.qualifications.partner',
        ),
        sortable: true,
        colId: 'partnerName',
      },
      {
        flex: 1,
        field: 'acquiredAt',
        headerName: this.translateService.instant('general.acquired_on'),
        cellRenderer: 'dateRendererComponent',
        sortable: true,
        colId: 'acquiredAt',
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
      },
      {
        flex: 1,
        field: 'validFrom',
        headerName: this.translateService.instant('general.valid_from'),
        cellRenderer: 'dateRendererComponent',
        sortable: true,
        colId: 'validFrom',
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
      },
      {
        flex: 1,
        field: 'validTill',
        headerName: this.translateService.instant('general.valid_until'),
        cellRenderer: 'validTillRenderer',
        sortable: true,
        colId: 'validTill',
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
      },
      {
        flex: 1,
        field: 'status',
        cellRenderer: 'statusIconRenderer',
        headerName: this.translateService.instant('general.status'),
        sortable: true,
        colId: 'status',
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
      },
      {
        flex: 1,
        field: 'confirmedByUser',
        cellRenderer: 'confirmedByUserIconRendererComponent',
        headerName: this.translateService.instant(
          'page.qualifications.confirmed',
        ),
        sortable: true,
        colId: 'confirmedByUser',
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
      },
      {
        flex: 1,
        field: 'qualification',
        cellRenderer: 'qualificationCategoryRendererComponent',
        headerName: this.translateService.instant(
          'page.qualifications.category',
        ),
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
        sortable: true,
        colId: 'qualificationCategoryName',
      },
    ];
  }

  ngOnInit(): void {
    combineLatest([
      this.statusFilter,
      this.categoryFilter,
      this.userIdsFilter,
      this.searchFilter,
      this.partnerFilter,
      this.translateService.onLangChange.pipe(
        startWith({ lang: this.translateService.getCurrentLang() ?? '' }),
        map((event) => ({ lang: event.lang.split('-')[0] })),
      ),
    ])
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        tap(([statuses, category, userIds, search, partnerIds, langEvent]) => {
          this.filters = {
            statuses,
            category,
            userIds,
            search,
            partnerIds,
            lang: langEvent.lang,
          };
        }),
      )
      .subscribe(() => {
        if (this.gridApi) {
          this.refreshGrid();
          this.store.dispatch(
            new ResetSharedUserQualificationsV2OverviewDetails(),
          );
        }
      });
  }

  onGridReady(params: { api: GridApi }): void {
    this.gridApi = params.api;
    this.gridApi.removeEventListener(
      'paginationChanged',
      this.paginationChangedListener,
    );
    this.gridApi.setGridOption(
      'datasource',
      this.createDataSource(this.defaultSortingFields),
    );
  }

  public handleOnCloseDetailsDrawer(): void {
    this.store.dispatch(
      new OpenSharedUserQualificationV2OverviewDetailsDrawer(false),
    );
  }

  private refreshGrid(): void {
    if (this.gridApi) {
      this.gridApi.removeEventListener(
        'paginationChanged',
        this.paginationChangedListener,
      );
      this.gridApi.setGridOption(
        'datasource',
        this.createDataSource(this.defaultSortingFields),
      );
      this.gridApi.resetColumnState();
    }
  }

  private createDataSource(
    sortFields?: Record<string, 'asc' | 'desc'> | null,
  ): {
    getRows: (params: IGetRowsParams) => void;
  } {
    return {
      getRows: (params: IGetRowsParams): void => {
        this.gridApi.removeEventListener(
          'paginationChanged',
          this.paginationChangedListener,
        );

        let pageSize = this.gridApi.paginationGetPageSize();
        const pageNumber = Math.floor(params.startRow / pageSize) + 1;

        const queryParams = this.activatedRoute.snapshot.queryParams;
        if (queryParams['pageSize']) {
          pageSize = queryParams['pageSize'] as number;
        }

        this.gridApi.updateGridOptions({
          cacheBlockSize: pageSize,
          paginationPageSize: pageSize,
        });

        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: {
            pageSize,
          },
          queryParamsHandling: 'merge',
        });

        if (pageNumber < 1) {
          params.failCallback();
          return;
        }

        this.gridApi.setGridOption('loading', true);
        this.showGrid = false;

        const whereParams = this.setWhereOptions(sortFields);

        this.store
          .dispatch(
            new GetSharedUserQualificationsV2Overview({
              pageNumber,
              pageSize,
              relations: ['qualification'],
              lang: this.filters.lang,
              ...whereParams,
            }),
          )
          .pipe(
            catchError(() => {
              params.failCallback();
              return of({ data: [], pagination: { totalRecords: 0 } });
            }),
            finalize(() => {
              this.gridApi.setGridOption('loading', false);
              this.showGrid = true;
              this.gridApi.addEventListener(
                'paginationChanged',
                this.paginationChangedListener,
              );
            }),
            switchMap(() =>
              combineLatest([
                this.store.select(
                  SharedUserQualificationsV2OverviewState.getSharedUserQualifications,
                ),
                this.store.select(
                  SharedUserQualificationsV2OverviewState.getTotalRecords,
                ),
              ]),
            ),
            take(1),
          )
          .subscribe(([qualifications, totalRecords]) => {
            if (qualifications?.length) {
              params.successCallback(qualifications, totalRecords);
            } else {
              params.successCallback([], 0);
              this.gridApi.setGridOption('loading', false);
              this.gridApi.showNoRowsOverlay();
            }
          });
      },
    };
  }

  private setWhereOptions(
    sortFields?: Record<string, 'asc' | 'desc'> | null,
  ): WhereOption {
    let whereOptions = {};

    if (this.filters.userIds?.length) {
      whereOptions = {
        ...whereOptions,
        userIds: this.filters.userIds,
      };
    }

    if (this.filters.category?.length) {
      whereOptions = {
        ...whereOptions,
        qualificationCategoryIds: this.filters.category,
      };
    }

    if (this.filters.partnerIds?.length) {
      whereOptions = {
        ...whereOptions,
        partnershipIds: this.filters.partnerIds,
      };
    }

    if (this.filters.search) {
      whereOptions = {
        ...whereOptions,
        searchText: this.filters.search,
      };
    }

    if (this.filters.statuses?.length) {
      whereOptions = {
        ...whereOptions,
        statuses: this.filters.statuses,
      };
    }

    if (sortFields && Object.keys(sortFields).length > 0) {
      whereOptions = {
        ...whereOptions,
        order: sortFields,
      };
    }

    whereOptions = {
      ...whereOptions,
      lang: this.filters.lang,
    };

    return whereOptions;
  }

  private handleQualificationClick(data: UserQualificationV2): void {
    this.selectedQualification = data;

    this.store
      .select(
        SharedUserQualificationsV2OverviewState.getSharedUserQualificationDetails,
      )
      .pipe(
        take(1),
        tap((currentDetails) => {
          if (this.isSameQualification(currentDetails)) {
            this.openDrawerWithExistingDetails();
          } else {
            this.fetchAndShowNewDetails(this.selectedQualification.id);
          }
        }),
      )
      .subscribe();
  }

  private openDrawerWithExistingDetails(): void {
    this.store.dispatch(
      new OpenSharedUserQualificationV2OverviewDetailsDrawer(true),
    );
  }

  private fetchAndShowNewDetails(userQualificationId: string): void {
    this.store.dispatch(
      new GetSharedUserQualificationsV2OverviewDetails(userQualificationId),
    );
  }

  private isSameQualification(
    currentDetails: SharedUserQualificationDetailViewResponse | null,
  ): boolean {
    return currentDetails?.id === this.selectedQualification.id;
  }

  private handleSortChanged(params: { api: GridApi }): void {
    const sortModel = params.api.getColumnState();
    const sortFields = sortModel
      .filter((sort) => sort.sort)
      .reduce(
        (acc, sort) => ({
          ...acc,
          [sort.colId]: sort.sort?.toLowerCase(),
        }),
        {},
      );

    this.refreshGridWithSort(sortFields);
  }

  private refreshGridWithSort(
    sortFields: Record<string, 'asc' | 'desc'> | null,
  ): void {
    if (this.gridApi) {
      this.gridApi.removeEventListener(
        'paginationChanged',
        this.paginationChangedListener,
      );
      const dataSource = this.createDataSource(sortFields);
      this.gridApi.setGridOption('datasource', dataSource);
    }
  }

  ngOnDestroy(): void {
    this.gridApi.removeEventListener(
      'paginationChanged',
      this.paginationChangedListener,
    );
    this.store.dispatch(new ResetSharedUserQualificationsV2OverviewDetails());
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handlePaginationChanged(): void {
    const pageSize = this.gridApi.paginationGetPageSize();

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        pageSize,
      },
      queryParamsHandling: 'merge',
    });

    this.gridApi.setGridOption('cacheBlockSize', pageSize);
  }
}
